import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import i18n from "~/i18n";
import { type DocumentVersionDetails } from "~/api/types";
import { type UpdateDocumentVersionData } from "~/api/v2/requests";
import { isSameDateValue } from "~/helpers/date";

interface Props {
  documentVersion: DocumentVersionDetails;
  field?: DocumentVersionField;
}

export type DocumentVersionField = "reviewFrom" | "reviewTo" | "adoptedAt";

const createFormSchema = (field?: DocumentVersionField) =>
  z
    .object({
      adoptedAt: z.date().nullable(),
      reviewFrom: z.date().nullable(),
      reviewTo: z.date().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.reviewTo && data.reviewFrom === null) {
        ctx.addIssue({
          code: "custom",
          message: i18n.t(($) => $.validations.startDateRequired),
          path: [field === "reviewTo" ? "reviewTo" : "reviewFrom"],
        });
      }

      if (
        data.reviewFrom !== null &&
        data.reviewTo !== null &&
        data.reviewTo <= data.reviewFrom
      ) {
        ctx.addIssue({
          code: "custom",
          message: i18n.t(($) => $.validations.endDateAfter),
          path: [field === "reviewFrom" ? "reviewFrom" : "reviewTo"],
        });
      }
    });

export type DocumentVersionEditFormValues = z.infer<
  ReturnType<typeof createFormSchema>
>;

const getDocumentVersionEditFormDefaultValues = (
  documentVersion: DocumentVersionDetails,
): DocumentVersionEditFormValues => ({
  adoptedAt: documentVersion.adoptedAt
    ? new Date(documentVersion.adoptedAt)
    : null,
  reviewFrom: documentVersion.reviewFrom
    ? new Date(documentVersion.reviewFrom)
    : null,
  reviewTo: documentVersion.reviewTo
    ? new Date(documentVersion.reviewTo)
    : null,
});

export function useDocumentVersionEditForm({ documentVersion, field }: Props) {
  const form = useForm<DocumentVersionEditFormValues>({
    values: getDocumentVersionEditFormDefaultValues(documentVersion),
    resolver: zodResolver(createFormSchema(field)),
  });

  const filterUnchangedValues = (
    values: DocumentVersionEditFormValues,
  ): UpdateDocumentVersionData => {
    const result: UpdateDocumentVersionData = {};

    if (!isSameDateValue(values.reviewFrom, documentVersion.reviewFrom)) {
      result.reviewFrom = values.reviewFrom;
    }

    if (!isSameDateValue(values.reviewTo, documentVersion.reviewTo)) {
      result.reviewTo = values.reviewTo;
    }

    if (!isSameDateValue(values.adoptedAt, documentVersion.adoptedAt)) {
      result.adoptedAt = values.adoptedAt;
    }

    return result;
  };

  return { form, filterUnchangedValues };
}
